import { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Collapse,
  InputAdornment,
  IconButton,
  TableSortLabel,
  Button,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";

const TAX_RATE = 0.07;

export default function DailyPurchaseSummary() {
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");

  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");

  const cellStyle = { border: "1px solid #ddd" };

  // ---------- Fetch: ใช้ getPurchaseTransactions ----------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getPurchaseTransactions({
          status: "payed",
          startDate,
          endDate,
        });

        const rawData = res?.data?.data ?? res?.data ?? res ?? [];

        const processed = (Array.isArray(rawData) ? rawData : [rawData]).map(
          (row) => {
            // เตรียม item เบื้องต้น (คิด amountBeforeVat)
            const items = (row.items ?? []).map((item) => {
              const netWeight = Number(item.netWeight ?? item.weight ?? 0);
              const pricePerUnit = Number(item.pricePerUnit ?? 0);
              const amountBeforeVat = netWeight * pricePerUnit;

              // เบื้องต้น: ถ้ายังไม่รู้ vat ระดับบิล ให้คิดจาก isVat
              const itemVat =
                row.vat != null
                  ? 0
                  : row.isVat
                    ? amountBeforeVat * TAX_RATE
                    : 0;

              return {
                ...item,
                productName: item.productName ?? item.product?.title ?? "-",
                netWeight,
                unit: item.unit ?? "กก.",
                pricePerUnit,
                amountBeforeVat,
                vat: itemVat,
                netPrice: Number(item.netPrice ?? amountBeforeVat + itemVat),
              };
            });

            // ถ้า backend ส่ง row.vat เป็นยอดรวมทั้งบิล ให้กระจายตามสัดส่วน
            if (row.vat != null && items.length) {
              const totalBefore = items.reduce(
                (acc, it) => acc + it.amountBeforeVat,
                0
              );
              const totalVatFromRow = Number(row.vat ?? 0);
              items.forEach((it) => {
                const share =
                  totalBefore > 0
                    ? (it.amountBeforeVat / totalBefore) * totalVatFromRow
                    : 0;
                it.vat = share;
                it.netPrice = it.amountBeforeVat + share;
              });
            }

            return { ...row, items };
          }
        );

        setRows(processed);
        setOpenSet(new Set());
      } catch (err) {
        console.error("fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  // ---------- Utils ----------
  const formatNum = (val) =>
    val != null
      ? Number(val).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "-";

  const formatDateTime = (datetimeStr) => {
    const date = new Date(datetimeStr);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatThaiDate = (datetimeStr) => {
    const date = new Date(datetimeStr);
    return date.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getRowMetrics = (row) => {
    const totalWeight =
      row.items?.reduce((acc, i) => acc + (i.netWeight || 0), 0) ?? 0;
    const beforeVat =
      row.items?.reduce((acc, i) => acc + (i.amountBeforeVat || 0), 0) ?? 0;
    const vat = row.items?.reduce((acc, i) => acc + (i.vat || 0), 0) ?? 0;
    const totalNet =
      row.items?.reduce((acc, i) => acc + (i.netPrice || 0), 0) ?? 0;
    const avgUnit =
      row.items?.length > 0
        ? row.items.reduce((acc, i) => acc + (i.pricePerUnit || 0), 0) /
          row.items.length
        : 0;
    return { totalWeight, beforeVat, vat, totalNet, avgUnit };
  };

  // ---------- Search ----------
  const filteredRows = useMemo(() => {
    const q = (searchQuery || "").toLowerCase().trim();
    if (!q) return rows;

    return rows.filter((row) => {
      const bill = (row.billNumber || "").toLowerCase();
      const customer =
        row.customer?.name?.toLowerCase() ||
        row.customerName?.toLowerCase() ||
        "";
      const dateTH = formatThaiDate(row.createdAt).toLowerCase();
      const dateISO = String(row.createdAt || "").toLowerCase();
      const productList = (row.items || [])
        .map((it) => (it.productName || "").toLowerCase())
        .join(" ");
      const car = `${row.car?.type || ""} ${row.car?.license || ""}`
        .trim()
        .toLowerCase();

      return (
        bill.includes(q) ||
        customer.includes(q) ||
        dateTH.includes(q) ||
        dateISO.includes(q) ||
        productList.includes(q) ||
        car.includes(q)
      );
    });
  }, [rows, searchQuery]);

  // ---------- Sort ----------
  const sortedRows = useMemo(() => {
    const data = [...filteredRows];
    data.sort((a, b) => {
      const mA = getRowMetrics(a);
      const mB = getRowMetrics(b);

      let aVal;
      let bVal;

      switch (orderBy) {
        case "billNumber":
          aVal = a.billNumber ?? "";
          bVal = b.billNumber ?? "";
          break;
        case "createdAt":
          aVal = new Date(a.createdAt).getTime() || 0;
          bVal = new Date(b.createdAt).getTime() || 0;
          break;
        case "customerName":
          aVal = (a.customer?.name ?? a.customerName ?? "").toLowerCase();
          bVal = (b.customer?.name ?? b.customerName ?? "").toLowerCase();
          break;
        case "totalWeight":
          aVal = mA.totalWeight;
          bVal = mB.totalWeight;
          break;
        case "beforeVat":
          aVal = mA.beforeVat;
          bVal = mB.beforeVat;
          break;
        case "vat":
          aVal = mA.vat;
          bVal = mB.vat;
          break;
        case "totalNet":
          aVal = mA.totalNet;
          bVal = mB.totalNet;
          break;
        default:
          aVal = 0;
          bVal = 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal, "th");
        return order === "asc" ? cmp : -cmp;
      } else {
        const cmp = (aVal || 0) - (bVal || 0);
        return order === "asc" ? cmp : -cmp;
      }
    });
    return data;
  }, [filteredRows, orderBy, order]);

  const requestSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      if (["billNumber", "customerName"].includes(key)) setOrder("asc");
      else setOrder("desc");
    }
  };

  const getRowKey = (row, idx) =>
    String(row.transactionId ?? row.id ?? row.billNumber ?? idx);

  const toggleOpen = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ---------- Export CSV ----------
  function exportToCSV(data, filename = "purchase-summary.csv") {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleExportMain = () => {
    const dataset = sortedRows.map((row) => {
      const { totalWeight, beforeVat, vat, totalNet, avgUnit } =
        getRowMetrics(row);

      return {
        เลขที่Batch: row.billNumber ?? "",
        วันที่จ่าย: formatDateTime(row.createdAt),
        ชื่อซัพพลายเออร์: row.customer?.name ?? row.customerName ?? "-",
        รถขนส่ง: row.car?.license
          ? `${row.car?.type || ""} ${row.car.license}`.trim()
          : "-",
        ประเภทซัพพลายเออร์:
          row.customer?.personType === "company"
            ? "นิติบุคคล"
            : row.customer?.personType === "normal"
              ? "บุคคลธรรมดา"
              : "-",
        "น้ำหนักรวม (กก.)": Number(totalWeight).toFixed(2),
        หน่วย: "กก.",
        "ราคาซื้อ/หน่วย": Number(avgUnit).toFixed(2),
        ยอดก่อนภาษี: Number(beforeVat).toFixed(2),
        ภาษี: Number(vat).toFixed(2),
        ยอดรวม: Number(totalNet).toFixed(2),
        ช่องทางจ่ายเงิน: row.paymentMethod ?? "-",
      };
    });

    exportToCSV(dataset, `purchase_tickets_${startDate}_to_${endDate}.csv`);
  };

  const handleExportRowItems = (row) => {
    const dataset = (row.items || []).map((it) => ({
      สินค้า: it.productName ?? "-",
      "น้ำหนัก (กก.)": Number(it.netWeight || 0).toFixed(2),
      หน่วย: it.unit ?? "กก.",
      "ราคาซื้อ/หน่วย": Number(it.pricePerUnit || 0).toFixed(2),
      ยอดก่อนภาษี: Number(it.amountBeforeVat || 0).toFixed(2),
      ภาษี: Number(it.vat || 0).toFixed(2),
      ยอดรวม: Number(it.netPrice || 0).toFixed(2),
    }));

    const fname = `ticket_${row.billNumber || row.id || "detail"}.csv`;
    exportToCSV(dataset, fname);
  };

  const handleExportAllItems = () => {
    const dataset = [];

    sortedRows.forEach((row) => {
      const common = {
        เลขที่Batch: row.billNumber ?? "",
        วันที่จ่าย: formatDateTime(row.createdAt),
        ชื่อซัพพลายเออร์: row.customer?.name ?? row.customerName ?? "-",
        รถขนส่ง: row.car?.license
          ? `${row.car?.type || ""} ${row.car.license}`.trim()
          : "-",
        ประเภทซัพพลายเออร์:
          row.customer?.personType === "company"
            ? "นิติบุคคล"
            : row.customer?.personType === "normal"
              ? "บุคคลธรรมดา"
              : "-",
        ช่องทางจ่ายเงิน: row.paymentMethod ?? "-",
        ชื่อบัญชี:
          (row.paymentMethod === "โอนเงิน" && row.customer?.bankAccountName) ??
          "-",
        ธนาคาร:
          (row.paymentMethod === "โอนเงิน" && row.customer?.bankName) ?? "-",
        เลขที่บัญชี:
          (row.paymentMethod === "โอนเงิน" &&
            row.customer?.bankAccountNumber) ??
          "-",
      };

      (row.items || []).forEach((it) => {
        dataset.push({
          ...common,
          สินค้า: it.productName ?? "-",
          "น้ำหนัก (กก.)": Number(it.netWeight || 0).toFixed(2),
          หน่วย: it.unit ?? "กก.",
          "ราคาซื้อ/หน่วย": Number(it.pricePerUnit || 0).toFixed(2),
          ยอดก่อนภาษี: Number(it.amountBeforeVat || 0).toFixed(2),
          ภาษี: Number(it.vat || 0).toFixed(2),
          ยอดรวม: Number(it.netPrice || 0).toFixed(2),
        });
      });
    });

    if (dataset.length === 0) return;
    exportToCSV(dataset, `purchase_items_${startDate}_to_${endDate}.csv`);
  };

  // ---------- Summary ----------
  const summary = useMemo(() => {
    let ticketCount = sortedRows.length;
    let totalWeight = 0;
    let totalBeforeVat = 0;
    let totalVat = 0;
    let totalNet = 0;

    sortedRows.forEach((row) => {
      const m = getRowMetrics(row);
      totalWeight += m.totalWeight;
      totalBeforeVat += m.beforeVat;
      totalVat += m.vat;
      totalNet += m.totalNet;
    });

    return {
      ticketCount,
      totalWeight,
      totalBeforeVat,
      totalVat,
      totalNet,
    };
  }, [sortedRows]);

  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <AppBreadcrumbs />
      <Box display="flex" justifyContent="flex-end" gap={2} my={2}>
        <TextField
          label="ค้นหา"
          value={searchQuery}
          placeholder="เลขที่Batch / วันที่จ่าย / ซัพพลายเออร์ / สินค้า / รถ"
          onChange={(e) => setSearchQuery(e.target.value)}
          size="medium"
          fullWidth
          sx={{ maxWidth: 520 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="เริ่มต้น"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="สิ้นสุด"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            my={1}
          >
            <Typography variant="h6" gutterBottom>
              สรุปยอดซื้อสินค้ารายBatch
            </Typography>

            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                size="large"
                onClick={handleExportMain}
              >
                ข้อมูลหลัก
              </Button>

              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                size="large"
                onClick={handleExportAllItems}
              >
                ข้อมูลย่อยทั้งหมด
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                <TableRow>
                  <TableCell sx={{ ...cellStyle, padding: "10px" }} />
                  <TableCell
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "billNumber" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "billNumber"}
                      direction={orderBy === "billNumber" ? order : "asc"}
                      onClick={() => requestSort("billNumber")}
                    >
                      เลขที่Batch
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "createdAt" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "createdAt"}
                      direction={orderBy === "createdAt" ? order : "desc"}
                      onClick={() => requestSort("createdAt")}
                    >
                      วันที่จ่าย
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "customerName" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "customerName"}
                      direction={orderBy === "customerName" ? order : "asc"}
                      onClick={() => requestSort("customerName")}
                    >
                      ชื่อซัพพลายเออร์
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellStyle}>รถขนส่ง</TableCell>
                  <TableCell sx={cellStyle}>ประเภทซัพพลายเออร์</TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "totalWeight" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "totalWeight"}
                      direction={orderBy === "totalWeight" ? order : "desc"}
                      onClick={() => requestSort("totalWeight")}
                    >
                      น้ำหนักรวม
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellStyle}>หน่วย</TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    ราคาซื้อ/หน่วย
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "beforeVat" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "beforeVat"}
                      direction={orderBy === "beforeVat" ? order : "desc"}
                      onClick={() => requestSort("beforeVat")}
                    >
                      ยอดก่อนภาษี
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "vat" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "vat"}
                      direction={orderBy === "vat" ? order : "desc"}
                      onClick={() => requestSort("vat")}
                    >
                      ภาษี
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "totalNet" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "totalNet"}
                      direction={orderBy === "totalNet" ? order : "desc"}
                      onClick={() => requestSort("totalNet")}
                    >
                      ยอดรวม
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellStyle}>ช่องทางจ่ายเงิน</TableCell>
                  <TableCell sx={cellStyle}>ชื่อบัญชี</TableCell>
                  <TableCell sx={cellStyle}>ธนาคาร</TableCell>
                  <TableCell sx={cellStyle}>เลขที่บัญชี</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.map((row, idx) => {
                  const key = getRowKey(row, idx);
                  const isOpen = openSet.has(key);
                  const { totalWeight, beforeVat, vat, totalNet, avgUnit } =
                    getRowMetrics(row);

                  return (
                    <>
                      <TableRow key={key} hover>
                        <TableCell sx={{ ...cellStyle, paddingBlock: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleOpen(key)}
                            aria-label="expand row"
                          >
                            {isOpen ? (
                              <KeyboardArrowDownIcon />
                            ) : (
                              <KeyboardArrowRightIcon />
                            )}
                          </IconButton>
                        </TableCell>

                        <TableCell sx={cellStyle}>{row.billNumber}</TableCell>
                        <TableCell sx={cellStyle}>
                          {formatDateTime(row.createdAt)}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {row.customer?.name ?? row.customerName ?? "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {row.car?.license
                            ? `${row.car?.type || ""} ${row.car.license}`.trim()
                            : "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {row.customer?.personType === "company"
                            ? "นิติบุคคล"
                            : row.customer?.personType === "normal"
                              ? "บุคคลธรรมดา"
                              : "-"}
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(totalWeight)}
                        </TableCell>
                        <TableCell sx={cellStyle}>กก.</TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(avgUnit)}
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(beforeVat)}
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(vat)}
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(totalNet)}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {row.paymentMethod ?? "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {(row.paymentMethod === "โอนเงิน" &&
                            row.customer?.bankAccountName) ??
                            ""}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {(row.paymentMethod === "โอนเงิน" &&
                            row.customer?.bankName) ??
                            ""}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {(row.paymentMethod === "โอนเงิน" &&
                            row.customer?.bankAccountNumber) ??
                            ""}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell style={{ padding: 0 }} colSpan={16}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box p={2}>
                              <Box
                                mb={1}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography variant="subtitle2">
                                  รายการสินค้า
                                </Typography>
                                <Button
                                  variant="outlined"
                                  size="large"
                                  startIcon={<ContentCopyIcon />}
                                  onClick={() => handleExportRowItems(row)}
                                >
                                  ข้อมูลย่อย
                                </Button>
                              </Box>

                              <Table>
                                <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                                  <TableRow>
                                    <TableCell sx={cellStyle}>สินค้า</TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      น้ำหนัก
                                    </TableCell>
                                    <TableCell sx={cellStyle}>หน่วย</TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      ราคาซื้อ/หน่วย
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      ยอดก่อนภาษี
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      ภาษี
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      ยอดรวม
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(row.items ?? []).map((p, i) => (
                                    <TableRow
                                      key={`item-${row.billNumber ?? i}-${i}`}
                                    >
                                      <TableCell sx={cellStyle}>
                                        {p.productName ?? "-"}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNum(p.netWeight)}
                                      </TableCell>
                                      <TableCell sx={cellStyle}>
                                        {p.unit ?? "กก."}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNum(p.pricePerUnit)}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNum(p.amountBeforeVat)}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNum(p.vat)}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNum(p.netPrice)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>

              {/* สรุปผลรวม */}
              <TableFooter>
                <TableRow
                  sx={{
                    backgroundColor: "#fafafa",
                    "& td": { fontSize: "14px", fontWeight: "bold" },
                  }}
                >
                  <TableCell sx={cellStyle} />
                  <TableCell sx={cellStyle} colSpan={5}>
                    ยอดรวม {summary.ticketCount.toLocaleString("th-TH")} Batch
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalWeight)}
                  </TableCell>
                  <TableCell sx={cellStyle}></TableCell>
                  <TableCell sx={cellStyle}></TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalBeforeVat)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalVat)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalNet)}
                  </TableCell>
                  <TableCell sx={cellStyle}></TableCell>
                  <TableCell sx={cellStyle}></TableCell>
                  <TableCell sx={cellStyle}></TableCell>
                  <TableCell sx={cellStyle}></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

/* ---------- helper for search ---------- */
function formatThaiDate(datetimeStr) {
  const date = new Date(datetimeStr);
  return date.toLocaleDateString("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
